/**
 * MTA-STS recommendation card for the agency-settings DNS tab.
 *
 * Shows the three DNS records the client needs to publish (or has already
 * published) to participate in Travel Minds' managed MTA-STS service:
 *   1) mta-sts.<domain> CNAME -> mta-sts.travelminds.com
 *   2) _mta-sts.<domain> TXT  v=STSv1; id=<policy_id>
 *   3) _smtp._tls.<domain> TXT v=TLSRPTv1; rua=mailto:tlsrpt@reports.travelminds.com
 *
 * Admins can enable/disable managed MTA-STS for the client from this card.
 * Until an admin enables it, the card shows the recommendation without an
 * active policy_id (records would still work but no managed policy file
 * is served until a row exists in client_mta_sts_policies).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  Radio,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { useUser } from '../../contexts/UserContext';
import {
  mtaStsApi,
  MtaStsCheck,
  MtaStsMode,
  MtaStsPolicy,
  MtaStsExpectedDns,
} from '../../services/mtaStsApi';

/**
 * Render a check / X / hourglass icon for a single DNS-record validation result.
 * Visual style matches `renderDnsStatusIcon` in pages/Settings.tsx so the
 * MTA-STS rows look like every other DNS row in the tab: a 24×24 colored
 * circle with a white icon inside.
 *   - yellow `?` : no result yet (not asked, or check hasn't returned)
 *   - blue hourglass : `loading` is true
 *   - green ✓ : check.ok === true
 *   - red ✕ : check.ok === false
 */
const CheckIcon: React.FC<{ check?: MtaStsCheck | null; loading?: boolean }> = ({
  check,
  loading,
}) => {
  const iconStyle = {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    color: '#fff',
  };

  if (loading) {
    return (
      <Tooltip title="Looking up DNS…">
        <Box sx={{ ...iconStyle, bgcolor: '#2196f3' }}>
          <HourglassEmptyIcon className="animate-spin" sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }
  if (!check || check.ok === null) {
    return (
      <Tooltip title="Not validated yet — click Validate DNS">
        <Box sx={{ ...iconStyle, bgcolor: '#ffd54f' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>?</Typography>
        </Box>
      </Tooltip>
    );
  }
  if (check.ok) {
    return (
      <Tooltip title={check.found || 'Matches expected value'}>
        <Box sx={{ ...iconStyle, bgcolor: '#4caf50' }}>
          <CheckCircleIcon sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={check.message || 'Does not match expected value'}>
      <Box sx={{ ...iconStyle, bgcolor: '#f44336' }}>
        <CancelIcon sx={{ fontSize: 16 }} />
      </Box>
    </Tooltip>
  );
};

const MODE_OPTIONS: { value: MtaStsMode; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'None',
    description:
      "Receivers ignore the policy. Use to wind a policy down gracefully — once max_age elapses on their cache, you can pull the DNS records too.",
  },
  {
    value: 'testing',
    label: 'Testing',
    description:
      'Receivers check TLS but still deliver if it fails, and send TLS-RPT reports about the failures. Default for new policies — observe for 30–60 days before promoting.',
  },
  {
    value: 'enforce',
    label: 'Enforce',
    description:
      "Receivers refuse to deliver if TLS handshake fails or the MX doesn't match the policy. The actual protection mode — only promote here once Testing has been clean.",
  },
];

interface Props {
  domain: string | null | undefined;
  clientId: number | null | undefined;
  /** Best-guess MX hosts derived from current DNS validation (optional). */
  observedMxHosts?: string[];
  /**
   * Monotonically-increasing counter the parent bumps when the page-level
   * "Validate DNS" button is clicked. The card revalidates its policy in
   * response so the user doesn't need a separate MTA-STS button.
   */
  validateTrigger?: number;
}

function fallbackExpectedDns(domain: string): MtaStsExpectedDns {
  return {
    cname: {
      name: `mta-sts.${domain}`,
      type: 'CNAME',
      value: 'mta-sts.travelminds.com.',
      note: 'Points the MTA-STS host at our managed serving infra.',
    },
    policy_txt: {
      name: `_mta-sts.${domain}`,
      type: 'TXT',
      value: '"v=STSv1; id=<will be generated when enabled>"',
      note: 'Tells receivers what policy version to expect. The id is generated when an admin enables managed MTA-STS for this domain.',
    },
    tlsrpt_txt: {
      name: `_smtp._tls.${domain}`,
      type: 'TXT',
      value: '"v=TLSRPTv1; rua=mailto:tlsrpt@reports.travelminds.com"',
      note: 'Optional but recommended — sends inbound-TLS failure reports to our pipeline.',
    },
  };
}

const StatusChip: React.FC<{ policy: MtaStsPolicy | null; loading: boolean }> = ({
  policy,
  loading,
}) => {
  if (loading) return <Chip size="small" label="Checking…" />;
  if (!policy)
    return (
      <Chip
        size="small"
        icon={<HelpOutlineIcon fontSize="small" />}
        label="Not enabled"
        variant="outlined"
      />
    );
  if (!policy.enabled) return <Chip size="small" label="Disabled" variant="outlined" />;
  if (policy.cert_status === 'failed')
    return (
      <Chip
        size="small"
        color="error"
        icon={<ErrorOutlineIcon fontSize="small" />}
        label="Cert failed — DNS not pointed yet?"
      />
    );
  if (policy.cert_status === 'pending')
    return <Chip size="small" color="warning" label="Cert pending" />;
  if (policy.last_validation_ok === false)
    return (
      <Chip
        size="small"
        color="warning"
        icon={<ErrorOutlineIcon fontSize="small" />}
        label="DNS mismatch"
      />
    );
  const label = policy.mode === 'enforce' ? 'Active (enforce)' : `Active (${policy.mode})`;
  return (
    <Chip
      size="small"
      color="success"
      icon={<CheckCircleIcon fontSize="small" />}
      label={label}
    />
  );
};

const MtaStsRecommendation: React.FC<Props> = ({
  domain,
  clientId,
  observedMxHosts,
  validateTrigger,
}) => {
  const { isAdmin } = useUser();
  const [policy, setPolicy] = useState<MtaStsPolicy | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Live DNS lookup independent of whether we manage a policy in the portal.
  // Drives the per-record check/X icons in the table — they should be honest
  // about what's actually published even before someone clicks "Enable".
  const [dnsCheck, setDnsCheck] = useState<{
    cname: MtaStsCheck;
    policy_txt: MtaStsCheck;
    tlsrpt_txt: MtaStsCheck;
  } | null>(null);
  const [dnsChecking, setDnsChecking] = useState(false);

  const load = useCallback(async () => {
    if (!domain) return;
    setLoading(true);
    setError(null);
    try {
      const p = await mtaStsApi.getByDomain(domain);
      setPolicy(p);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setPolicy(null);
      } else {
        setError(e?.message || 'Failed to load MTA-STS policy');
      }
    } finally {
      setLoading(false);
    }
  }, [domain]);

  const refreshDnsCheck = useCallback(async () => {
    if (!domain) return;
    setDnsChecking(true);
    try {
      const result = await mtaStsApi.checkDns(domain);
      setDnsCheck(result);
    } catch {
      // Silently — the per-row icons fall back to the stored policy checks
      // (or the yellow ? placeholder) if the live lookup fails. We don't want
      // a fetch hiccup to red-bar the whole card.
    } finally {
      setDnsChecking(false);
    }
  }, [domain]);

  useEffect(() => {
    load();
    refreshDnsCheck();
  }, [load, refreshDnsCheck]);

  const handleEnable = async () => {
    if (!domain || !isAdmin()) return;
    setBusy(true);
    setError(null);
    try {
      const mxHosts =
        observedMxHosts && observedMxHosts.length > 0
          ? observedMxHosts
          : ['*.mail.protection.outlook.com'];
      const p = await mtaStsApi.create({
        domain,
        client_id: clientId ?? null,
        mode: 'testing',
        mx_hosts: mxHosts,
        max_age_seconds: 604800,
        enabled: true,
      });
      setPolicy(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to enable MTA-STS');
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = useCallback(async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    try {
      const p = await mtaStsApi.validate(policy.id);
      setPolicy(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Validation failed');
    } finally {
      setBusy(false);
    }
  }, [policy]);

  // When the parent page bumps validateTrigger ("Validate DNS" button):
  //   - always refresh the plain DNS check so the table icons stay current
  //   - if a stored policy exists, also re-run the policy validation so the
  //     header status chip + the alert above the table get an updated read
  // Skip the initial mount (validateTrigger=0).
  useEffect(() => {
    if (!validateTrigger) return;
    refreshDnsCheck();
    if (policy) handleValidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateTrigger]);

  const handleDelete = async () => {
    if (!policy || !isAdmin()) return;
    if (
      !confirm(
        `Remove managed MTA-STS for ${policy.domain}? The on-disk policy file will be deleted and the row removed from the portal. You'll also need to remove the CNAME and TXT records from DNS to stop receivers caching the old policy. To wind down gracefully, set Mode = "None" first and wait for max_age to expire.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await mtaStsApi.remove(policy.id);
      setPolicy(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to disable');
    } finally {
      setBusy(false);
    }
  };

  const handleModeChange = async (newMode: MtaStsMode) => {
    if (!policy || !isAdmin() || newMode === policy.mode) return;
    setBusy(true);
    setError(null);
    try {
      const p = await mtaStsApi.update(policy.id, {
        domain: policy.domain,
        client_id: policy.client_id,
        mode: newMode,
        mx_hosts: policy.mx_hosts,
        max_age_seconds: policy.max_age_seconds,
        enabled: policy.enabled,
      });
      setPolicy(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update mode');
    } finally {
      setBusy(false);
    }
  };

  if (!domain) return null;

  const expected = policy?.expected_dns ?? fallbackExpectedDns(domain);
  const records: { key: keyof typeof expected; record: typeof expected.cname }[] = [
    { key: 'cname', record: expected.cname },
    { key: 'policy_txt', record: expected.policy_txt },
    { key: 'tlsrpt_txt', record: expected.tlsrpt_txt },
  ];
  // Prefer the live DNS lookup (always fresh, works without an enabled policy).
  // Fall back to the stored policy validation if the live lookup hasn't returned
  // yet — that preserves the previous behavior while the icons populate.
  const checks: {
    cname: MtaStsCheck | null;
    policy_txt: MtaStsCheck | null;
    tlsrpt_txt: MtaStsCheck | null;
  } = dnsCheck
    ? dnsCheck
    : policy?.last_validation_checks
      ? {
          cname: policy.last_validation_checks.cname,
          policy_txt: policy.last_validation_checks.policy_txt,
          tlsrpt_txt: policy.last_validation_checks.tlsrpt_txt,
        }
      : { cname: null, policy_txt: null, tlsrpt_txt: null };

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <ShieldIcon color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          MTA-STS (managed)
        </Typography>
        <StatusChip policy={policy} loading={loading} />
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        MTA-STS protects your inbound mail from TLS-downgrade attacks. Senders that support
        MTA-STS (Gmail, Microsoft 365, etc.) will refuse to deliver to a configured domain
        over plaintext. Travel Minds hosts the policy file for you; the client side is just three
        DNS records.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {policy?.last_validation_message && (
        <Alert
          severity={policy.last_validation_ok ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          icon={policy.last_validation_ok ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
        >
          {policy.last_validation_ok
            ? 'DNS matches the served policy.'
            : policy.last_validation_message}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Required DNS records (publish these in your DNS provider):
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell width={40} />
              <TableCell width={40} align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map(({ key, record: r }) => (
              <TableRow key={r.name + r.type} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.name}</TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
                  {r.value}
                </TableCell>
                <TableCell>
                  <Tooltip title="Copy value">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(r.value)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <CheckIcon check={checks[key]} loading={dnsChecking && !checks[key]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1 }}>
        {records.map(r =>
          r.note ? (
            <Typography
              key={r.name + 'note'}
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              <strong>{r.name}:</strong> {r.note}
            </Typography>
          ) : null,
        )}
      </Box>

      {isAdmin() && (
        <>
          <Divider sx={{ my: 2 }} />
          {!policy ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleEnable}
              disabled={busy || loading}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ShieldIcon />}
            >
              Enable managed MTA-STS
            </Button>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Mode
              </Typography>
              <Stack spacing={1}>
                {MODE_OPTIONS.map(opt => (
                  <Paper
                    key={opt.value}
                    variant="outlined"
                    onClick={() => !busy && handleModeChange(opt.value)}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      cursor: busy ? 'default' : 'pointer',
                      borderColor: policy.mode === opt.value ? 'primary.main' : 'divider',
                      bgcolor: policy.mode === opt.value ? 'action.hover' : 'transparent',
                      '&:hover': busy ? {} : { borderColor: 'primary.main' },
                    }}
                  >
                    <Radio
                      size="small"
                      checked={policy.mode === opt.value}
                      disabled={busy}
                      sx={{ p: 0.5, mt: -0.25 }}
                    />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {opt.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Changing mode bumps the policy_id; update the{' '}
                  <code>_mta-sts.{policy.domain}</code> TXT to match (see records above).
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Served host: <code>{policy.served_host}</code> · policy_id:{' '}
                  <code>{policy.policy_id}</code> · cert:{' '}
                  <strong>{policy.cert_status}</strong>
                  {policy.cert_last_error && ` (${policy.cert_last_error})`}
                </Typography>
                <Box flex={1} />
                <Link
                  component="button"
                  variant="caption"
                  color="error"
                  underline="hover"
                  onClick={handleDelete}
                  disabled={busy}
                  sx={{ cursor: busy ? 'default' : 'pointer' }}
                >
                  Remove policy entirely
                </Link>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default MtaStsRecommendation;
